import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useT } from '../../i18n';

// ─────────────────────────────────────────────────────────────────────
// PhoneFrame — a smartphone-shaped preview shell.
//
// The mobile app itself (MobileApp) is a plain full-height component that
// knows nothing about this frame. The frame exists ONLY so the app can be
// previewed inside the desktop web app at a realistic size; when it ships as
// an actual app, MobileApp mounts on its own and this file is not used.
// ─────────────────────────────────────────────────────────────────────

export interface PhoneFrameProps {
  children: ReactNode;
  /** CSS width/height of the screen area (defaults to an iPhone-class 390×844). */
  width?: number;
  height?: number;
  /** Optional label rendered above the device (e.g. «Forhåndsvisning»). */
  label?: ReactNode;
  /** Leave the preview — rendered OUTSIDE the phone, so the app's own back arrow stays in-app. */
  onExit?: () => void;
}

export function PhoneFrame({
  children,
  width = 390,
  height = 844,
  label,
  onExit,
}: PhoneFrameProps) {
  const t = useT();
  const [clock, setClock] = useState(() => hhmm(new Date()));

  // «Touch» feel for the mouse: a soft fingertip follows the pointer over the
  // screen and every press leaves a fading ripple — so the preview reads like
  // a phone being tapped, not a web page being clicked.
  // The fingertip is moved straight on the DOM (ref + transform) — never via
  // React state — so mouse movement costs no re-render and stays smooth.
  const fingerRef = useRef<HTMLDivElement>(null);
  const [ripples, setRipples] = useState<{ id: number; x: number; y: number }[]>([]);
  const screenPos = (e: ReactPointerEvent) => {
    const r = e.currentTarget.getBoundingClientRect();
    // The frame may be scaled — map back to unscaled screen coordinates.
    const sx = r.width / width;
    return { x: (e.clientX - r.left) / sx, y: (e.clientY - r.top) / sx };
  };
  const onScreenPointerMove = (e: ReactPointerEvent) => {
    if (e.pointerType === 'touch') return;
    const el = fingerRef.current;
    if (!el) return;
    const p = screenPos(e);
    el.style.transform = `translate(${p.x}px, ${p.y}px) translate(-50%, -50%)`;
    el.style.opacity = '1';
  };
  const onScreenPointerLeave = () => {
    if (fingerRef.current) fingerRef.current.style.opacity = '0';
  };
  const onScreenPointerDown = (e: ReactPointerEvent) => {
    const p = screenPos(e);
    const id = Date.now() + Math.random();
    setRipples((rs) => [...rs.slice(-4), { id, ...p }]);
    setTimeout(() => setRipples((rs) => rs.filter((x) => x.id !== id)), 550);
  };
  useEffect(() => {
    const id = setInterval(() => setClock(hhmm(new Date())), 15000);
    return () => clearInterval(id);
  }, []);

  // Scale the device down when the viewport is shorter than the frame, so the
  // whole phone is always visible (the app inside keeps its own scroll).
  const [scale, setScale] = useState(1);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const fit = () => {
      const avail = window.innerHeight - 150; // header + breathing room
      setScale(Math.min(1, Math.max(0.55, avail / (height + 28))));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [height]);

  return (
    <div className="flex flex-col items-center gap-3 py-2">
      {(label || onExit) && (
        <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-wider text-ink-3">
          {label && <span>{label}</span>}
          {onExit && (
            <button onClick={onExit} className="underline hover:text-ink">
              {t('mobile.phoneFrame.close')}
            </button>
          )}
        </div>
      )}
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'top center',
          height: (height + 28) * scale,
        }}
      >
        {/* bezel */}
        <div
          className="relative"
          style={{
            width: width + 28,
            height: height + 28,
            borderRadius: 54,
            background: '#0b0f0c',
            boxShadow: '0 30px 60px -20px rgba(0,0,0,0.45), inset 0 0 0 2px #2a2f2b',
            padding: 14,
          }}
        >
          {/* screen */}
          <div
            className="relative overflow-hidden"
            style={{ width, height, borderRadius: 42, background: '#f7f8f5', cursor: 'none' }}
            onPointerMove={onScreenPointerMove}
            onPointerLeave={onScreenPointerLeave}
            onPointerDown={onScreenPointerDown}
          >
            <style>{`@keyframes sp-touch-ripple { from { transform: translate(-50%,-50%) scale(0.4); opacity: 0.55; } to { transform: translate(-50%,-50%) scale(1.6); opacity: 0; } }`}</style>
            {/* touch layer: fingertip + ripples (never intercepts input) */}
            <div className="absolute inset-0" style={{ zIndex: 40, pointerEvents: 'none' }}>
              <div
                ref={fingerRef}
                className="absolute rounded-full"
                style={{
                  left: 0,
                  top: 0,
                  width: 26,
                  height: 26,
                  opacity: 0,
                  transform: 'translate(-50%,-50%)',
                  willChange: 'transform',
                  background: 'rgba(15,22,17,0.18)',
                  border: '1.5px solid rgba(15,22,17,0.35)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                }}
              />
              {ripples.map((r) => (
                <div
                  key={r.id}
                  className="absolute rounded-full"
                  style={{
                    left: r.x,
                    top: r.y,
                    width: 56,
                    height: 56,
                    background: 'rgba(194,240,47,0.55)',
                    animation: 'sp-touch-ripple 0.5s ease-out forwards',
                  }}
                />
              ))}
            </div>
            {/* status bar */}
            <div
              className="absolute top-0 left-0 right-0 flex items-center justify-between px-7 font-semibold text-ink"
              style={{ height: 44, fontSize: 14, zIndex: 20, pointerEvents: 'none' }}
            >
              <span className="tabular-nums">{clock}</span>
              <span className="flex items-center gap-1.5 text-[11px]">
                <span aria-hidden>●●●</span>
                <span
                  className="inline-block rounded-sm"
                  style={{
                    width: 22,
                    height: 10,
                    border: '1.5px solid #0f1611',
                    position: 'relative',
                  }}
                >
                  <span
                    className="absolute rounded-[1px]"
                    style={{ left: 2, top: 2, bottom: 2, width: 13, background: '#0f1611' }}
                  />
                </span>
              </span>
            </div>
            {/* dynamic island */}
            <div
              className="absolute left-1/2 -translate-x-1/2 rounded-full"
              style={{ top: 11, width: 118, height: 30, background: '#0b0f0c', zIndex: 21 }}
            />
            {/* app */}
            <div className="absolute inset-0" style={{ paddingTop: 44 }}>
              {children}
            </div>
            {/* home indicator */}
            <div
              className="absolute left-1/2 -translate-x-1/2 rounded-full"
              style={{
                bottom: 8,
                width: 132,
                height: 5,
                background: '#0f1611',
                opacity: 0.85,
                zIndex: 20,
                pointerEvents: 'none',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function hhmm(d: Date) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
